/**
 * Hook for managing traffic cascade updates in forms
 * Implements Excel's automatic VLOOKUP behavior when traffic load fields change
 */
import { useCallback, useEffect } from 'react';
import { FormikProps } from 'formik';
import { calculateReliabilityLevelSuggested } from '../services/ProjectTypeService';

interface ITrafficLoadValues {
    functionalClassification?: string;
    roadType?: string;
    reliabilityLevelSuggested?: string;
}

interface FormValues {
    trafficLoad?: ITrafficLoadValues;

    [key: string]: unknown;
}

type FormikLikeFieldValue = string | number | boolean | undefined;
type SetFieldValueFn = (field: string, value: FormikLikeFieldValue, shouldValidate?: boolean) => void | Promise<void>;

/**
 * Traffic cascade hook for dynamic form management
 * Handles automatic reliability level suggested updates
 * Replicates Excel's automatic VLOOKUP updates when traffic classification changes
 */
export const useFormTrafficCascade = <T extends FormValues>(formik: FormikProps<T> | null) => {
    // Extract specific formik properties with null safety
    const setFieldValue = formik?.setFieldValue;
    const values = formik?.values;

    /**
     * Handle functional classification or road type change and update reliability level suggested
     * Excel Logic: D14 or D15 change → D16 update (VLOOKUP formula)
     */
    const handleTrafficClassificationChange = useCallback(
        (functionalClassification?: string, roadType?: string) => {
            // Early return if formik not properly initialized
            if (!formik || !setFieldValue || !values) {
                return;
            }

            const trafficLoad = values.trafficLoad;

            // Use provided values or fall back to current form values (nested trafficLoad)
            const currentClassification = functionalClassification ?? trafficLoad?.functionalClassification;
            const currentRoadType = roadType ?? trafficLoad?.roadType;

            // Only update if both values are available
            if (!currentClassification || !currentRoadType) {
                return;
            }

            // Calculate reliability level suggested using Excel VLOOKUP logic
            const reliabilityLevelSuggested = calculateReliabilityLevelSuggested({
                functionalClassification: currentClassification,
                roadType: currentRoadType,
            });

            // Update form value using setFieldValue for proper Formik integration
            // Use the full path since reliability field is in trafficLoad group
            setFieldValue('trafficLoad.reliabilityLevelSuggested', reliabilityLevelSuggested);
        },
        [formik, setFieldValue, values]
    );

    /**
     * Handle functional classification change specifically
     */
    const handleFunctionalClassificationChange = useCallback(
        (functionalClassification: string, providedSetFieldValue: SetFieldValueFn, currentValues: T) => {
            // Early return if required parameters not provided
            if (!providedSetFieldValue || !currentValues) {
                return;
            }

            // Read roadType from nested trafficLoad object
            const roadType = currentValues.trafficLoad?.roadType;
            handleTrafficClassificationChange(functionalClassification, roadType);
        },
        [handleTrafficClassificationChange]
    );

    /**
     * Handle road type change specifically
     */
    const handleRoadTypeChange = useCallback(
        (roadType: string, providedSetFieldValue: SetFieldValueFn, currentValues: T) => {
            // Early return if required parameters not provided
            if (!providedSetFieldValue || !currentValues) {
                return;
            }

            // Read functionalClassification from nested trafficLoad object
            const functionalClassification = currentValues.trafficLoad?.functionalClassification;
            handleTrafficClassificationChange(functionalClassification, roadType);
        },
        [handleTrafficClassificationChange]
    );

    // Effect to calculate initial value when form is first loaded
    // 🔧 FIX: Read from nested trafficLoad structure (not top-level)
    useEffect(() => {
        const trafficLoad = values?.trafficLoad;
        if (formik && trafficLoad?.functionalClassification && trafficLoad?.roadType) {
            // Only set if the field is currently empty or undefined
            const currentValue = trafficLoad?.reliabilityLevelSuggested;
            if (!currentValue) {
                handleTrafficClassificationChange(trafficLoad.functionalClassification, trafficLoad.roadType);
            }
        }
    }, [formik, values, handleTrafficClassificationChange]);

    return {
        handleTrafficClassificationChange,
        handleFunctionalClassificationChange,
        handleRoadTypeChange,
    };
};

export default useFormTrafficCascade;
